import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Flex, 
  Text, 
  Button, 
  TextField, 
  TextArea,
  Select, 
  Card, 
  Badge, 
  Dialog, 
  ScrollArea,
  Switch,
  Separator,
  Heading,
  Checkbox
} from '@radix-ui/themes';
import * as Tabs from '@radix-ui/react-tabs';
import { MagnifyingGlassIcon, UploadIcon, ExclamationTriangleIcon, Pencil1Icon, CheckIcon, Cross2Icon } from '@radix-ui/react-icons';
import { getAuthHeaders } from '../../../config/api.config';
import { toggleEnvironment } from '../../../utils/apiClient';
import { PDFExportButton } from '../../../components/PDFExport';
import { SubmissionCSVExportButton } from '../../../components/CSVExport';
import TopBar from '../../../components/ui/TopBar/TopBar';


interface Submission {
  id: string;
  student: {
    id: string;
    name: string;
    email: string;
  };
  class: {
    id: string;
    name: string;
  };
  totalScore: number;
  submittedAt: string;
  status: string;
  submissions: Array<{
    question: string;
    marks: number;
    score: number;
    answer: string;
    submittedAt: string;
    contentModeration?: {
      isInappropriate: boolean;
      reason: string;
      analysis: {
        isGenuineAttempt: boolean;
        containsProfanity: boolean;
      };
    };
    feedback?: {
      aiComment: string;
      suggestion: string;
      markRange: string;
      gradedAt: string | null;
    };
  }>;
}

const TeacherSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSubmission, setEditedSubmission] = useState<Submission | null>(null);
  const [sortBy, setSortBy] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState<string[]>([]);
  const navigate = useNavigate();

  // Get user data from localStorage
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/v1/stem@home/question/submissions/teacher/${user.userId}`,
        { 
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders() 
        }
      );
      
      setSubmissions(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions');
      setLoading(false);
    }
  }, [user.userId]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleViewSubmission = (submission: Submission) => {
    setSelectedSubmission(submission);
    setShowDetailsModal(true);
  };

  const handleEditSubmission = (submission: Submission) => {
    // Create a deep copy to preserve all fields
    const deepCopy = {
      ...submission,
      submissions: submission.submissions.map(sub => ({
        ...sub,
        marks: sub.marks, // Explicitly preserve marks
        score: sub.score, // Explicitly preserve score
        feedback: sub.feedback ? {
          ...sub.feedback
        } : undefined
      }))
    };
    
    setEditedSubmission(deepCopy);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedSubmission(null);
    setScoreErrors({});
  };

  const [scoreErrors, setScoreErrors] = useState<{[key: number]: string}>({});

  const handleScoreChange = (questionIndex: number, newScore: string) => {
    if (!editedSubmission) return;
    
    // Handle empty input
    if (newScore === '' || newScore === null || newScore === undefined) {
      const updatedSubmissions = [...editedSubmission.submissions];
      updatedSubmissions[questionIndex] = {
        ...updatedSubmissions[questionIndex], // Preserve all existing fields including marks
        score: 0
      };
      
      setEditedSubmission({
        ...editedSubmission,
        submissions: updatedSubmissions
      });
      return;
    }
    
    const score = parseInt(newScore);
    const maxScore = editedSubmission.submissions[questionIndex].marks || 0;
    
    // Validate score
    if (isNaN(score) || score < 0 || score > maxScore) {
      setScoreErrors(prev => ({
        ...prev,
        [questionIndex]: `Score must be between 0 and ${maxScore}`
      }));
    } else {
      // Clear error if valid
      setScoreErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionIndex];
        return newErrors;
      });
    }
    
    const updatedSubmissions = [...editedSubmission.submissions];
    updatedSubmissions[questionIndex] = {
      ...updatedSubmissions[questionIndex], // Preserve all existing fields including marks
      score: isNaN(score) ? 0 : score
    };
    
    setEditedSubmission({
      ...editedSubmission,
      submissions: updatedSubmissions
    });
  };

  const handleFeedbackChange = (questionIndex: number, field: string, value: string) => {
    if (!editedSubmission) return;
    
    const updatedSubmissions = [...editedSubmission.submissions];
    updatedSubmissions[questionIndex] = {
      ...updatedSubmissions[questionIndex], // Preserve all existing fields including marks
      feedback: {
        aiComment: updatedSubmissions[questionIndex].feedback?.aiComment || '',
        suggestion: updatedSubmissions[questionIndex].feedback?.suggestion || '',
        markRange: updatedSubmissions[questionIndex].feedback?.markRange || '',
        gradedAt: updatedSubmissions[questionIndex].feedback?.gradedAt || null,
        [field]: value
      }
    };
    
    setEditedSubmission({
      ...editedSubmission,
      submissions: updatedSubmissions
    });
  };

  const handleSaveEditedSubmission = async () => {
    if (!editedSubmission) {
      console.error('No editedSubmission found!');
      return;
    }
    
    // Check if we have any validation errors
    if (Object.keys(scoreErrors).length > 0) {
      console.error('Cannot save with validation errors:', scoreErrors);
      return;
    }
    
    // Try different possible ID field names
    const submissionId = editedSubmission.id || 
                        (editedSubmission as any).id || 
                        selectedSubmission?.id || 
                        (selectedSubmission as any)?.id;
    
    if (!submissionId) {
      console.error('No submission ID found!');
      return;
    }
    
          try {
        const editedData = {
          submissionId: submissionId,
          submissions: editedSubmission.submissions.map((sub, index) => ({
            index: index,
            score: sub.score,
            feedback: sub.feedback
          })),
          totalScore: editedSubmission.submissions.reduce((sum, sub) => sum + (sub.score || 0), 0)
        };
        
        const response = await axios.put(
          `/api/student/submissions/${editedData.submissionId}`,
          editedData,
          { 
            baseURL: toggleEnvironment(),
            headers: getAuthHeaders() 
          }
        );
        
        // Refresh the page to get fresh data from server
        window.location.reload();
        
      } catch (error) {
        console.error('Error updating submission:', error);
        if (error && typeof error === 'object' && 'response' in error) {
          console.error('Error details:', (error as any).response?.data);
        }
        // You might want to show an error message to the user here
      }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'No date available';
    
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-GB');
      const timeStr = date.toLocaleTimeString('en-GB');
      return `${dateStr}, ${timeStr}`;
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getSortedAndFilteredSubmissions = () => {
    let filteredSubmissions = [...submissions];

    // Apply flag filter
    if (showFlaggedOnly) {
      filteredSubmissions = filteredSubmissions.filter(submission => {
        return submission.submissions.some(sub => 
          sub.contentModeration?.isInappropriate === true
        );
      });
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredSubmissions = filteredSubmissions.filter(submission => 
        submission.student?.name?.toLowerCase().includes(query) ||
        submission.class?.name?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        return filteredSubmissions.sort((a, b) => 
          new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime()
        );
      case 'oldest':
        return filteredSubmissions.sort((a, b) => 
          new Date(a.submittedAt || 0).getTime() - new Date(b.submittedAt || 0).getTime()
        );
      case 'score-high':
        return filteredSubmissions.sort((a, b) => {
          const scoreA = a.submissions.reduce((sum, sub) => sum + sub.score, 0);
          const scoreB = b.submissions.reduce((sum, sub) => sum + sub.score, 0);
          return scoreB - scoreA;
        });
      case 'score-low':
        return filteredSubmissions.sort((a, b) => {
          const scoreA = a.submissions.reduce((sum, sub) => sum + sub.score, 0);
          const scoreB = b.submissions.reduce((sum, sub) => sum + sub.score, 0);
          return scoreA - scoreB;
        });
      default:
        return filteredSubmissions;
    }
  };

  const getScoreColor = (score: number, total: number) => {
    const percentage = (score / total) * 100;
    if (percentage >= 75) return 'bg-green-100 text-green-800'; // High: 75% – 100%
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800'; // Medium: 50% – 74%
    return 'bg-red-100 text-red-800'; // Low: 0% – 49%
  };

  const calculateTotalScore = (submissions: any[]) => {
    const totalScore = submissions.reduce((sum, sub) => sum + sub.score, 0);
    const totalMarks = submissions.reduce((sum, sub) => {
      return sum + (parseFloat(sub.marks) || 0);
    }, 0);
    return { totalScore, totalMarks };
  };

  // Selection handlers for CSV export
  const handleSubmissionSelection = (submissionId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedSubmissions(prev => [...prev, submissionId]);
    } else {
      setSelectedSubmissions(prev => prev.filter(id => id !== submissionId));
    }
  };

  if (loading) {
    return (
      <Box className="p-8 w-full min-h-screen">
        <Box className="text-center">
          <Text size="4" className="text-gray-600">Loading submissions...</Text>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="p-8 w-full min-h-screen">
        <Box className="text-center">
          <Text size="4" className="text-red-600">{error}</Text>
        </Box>
      </Box>
    );
  }

  const sortedAndFilteredSubmissions = getSortedAndFilteredSubmissions();

  // Selection handlers that depend on sortedAndFilteredSubmissions
  const handleSelectAllSubmissions = (isChecked: boolean) => {
    if (isChecked) {
      const allSubmissionIds = sortedAndFilteredSubmissions.map(submission => submission.id);
      setSelectedSubmissions(prev => {
        const newSelection = [...prev];
        allSubmissionIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    } else {
      const currentSubmissionIds = sortedAndFilteredSubmissions.map(submission => submission.id);
      setSelectedSubmissions(prev => prev.filter(id => !currentSubmissionIds.includes(id)));
    }
  };

  const isAllCurrentSubmissionsSelected = () => {
    const currentSubmissionIds = sortedAndFilteredSubmissions.map(submission => submission.id);
    return currentSubmissionIds.length > 0 && currentSubmissionIds.every(id => selectedSubmissions.includes(id));
  };

  return (
    <>
      <TopBar 
        title={`All Submissions (${submissions.length})`}
        showActions={true} 
      />
      <Box className="p-4 md:p-8 w-full min-h-screen" style={{ paddingTop: '96px' }}>

      {/* Controls Section - Mobile Responsive */}
      <Box className="mb-6">
        {/* Mobile Layout - Stacked */}
        <Box className="block md:hidden">
          <Flex direction="column" gap="4">
            {/* Search Bar */}
            <Box className="relative w-full">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <TextField.Root
                placeholder="Search by student or class name"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
                size="3"
              />
            </Box>
            
            {/* Controls Row */}
            <Flex align="center" gap="3" className="flex-wrap">
              <Select.Root value={sortBy} onValueChange={setSortBy}>
                <Select.Trigger className="min-w-[140px] flex-1" />
                <Select.Content>
                  <Select.Item value="recent">Most Recent</Select.Item>
                  <Select.Item value="oldest">Oldest First</Select.Item>
                  <Select.Item value="score-high">Highest Score</Select.Item>
                  <Select.Item value="score-low">Lowest Score</Select.Item>
                </Select.Content>
              </Select.Root>
              
              <Flex align="center" gap="2" className="bg-gray-50 px-3 py-2 rounded-lg">
                <ExclamationTriangleIcon className="w-4 h-4 text-gray-600" />
                <Text size="2" className="text-gray-600">Flagged</Text>
                <Switch
                  checked={showFlaggedOnly}
                  onCheckedChange={setShowFlaggedOnly}
                  size="1"
                />
              </Flex>
            </Flex>
            
            {/* CSV Export Buttons */}
            <Flex align="center" gap="2">
              <SubmissionCSVExportButton
                submissions={submissions}
                selectedSubmissions={selectedSubmissions}
                variant="all"
                className="flex-1"
              />
              <SubmissionCSVExportButton
                submissions={submissions}
                selectedSubmissions={selectedSubmissions}
                variant="selected"
                className="flex-1"
              />
            </Flex>

            {/* Upload Button */}
            <Button
              onClick={() => navigate('/upload-student-work')}
              size="3"
              className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 w-full"
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              Upload Student Work
            </Button>
          </Flex>
        </Box>

        {/* Desktop Layout - Horizontal */}
        <Flex justify="between" align="center" gap="4" className="hidden md:flex">
          <Flex align="center" gap="4" className="flex-1">
            <Box className="relative w-80">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <TextField.Root
                placeholder="Search by student or class name"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
                size="3"
              />
            </Box>
            
            <Flex align="center" gap="4">
              <Select.Root value={sortBy} onValueChange={setSortBy}>
                <Select.Trigger className="min-w-[200px]" />
                <Select.Content>
                  <Select.Item value="recent">Most Recent</Select.Item>
                  <Select.Item value="oldest">Oldest First</Select.Item>
                  <Select.Item value="score-high">Highest Score</Select.Item>
                  <Select.Item value="score-low">Lowest Score</Select.Item>
                </Select.Content>
              </Select.Root>
              
              <Flex align="center" gap="2">
                <ExclamationTriangleIcon className="w-4 h-4 text-gray-600" />
                <Text size="3" className="text-gray-600">Flagged</Text>
                <Switch
                  checked={showFlaggedOnly}
                  onCheckedChange={setShowFlaggedOnly}
                />
              </Flex>
            </Flex>
          </Flex>

          <Flex align="center" gap="3">
            <SubmissionCSVExportButton
              submissions={submissions}
              selectedSubmissions={selectedSubmissions}
              variant="all"
              className="mr-2"
            />
            <SubmissionCSVExportButton
              submissions={submissions}
              selectedSubmissions={selectedSubmissions}
              variant="selected"
              className="mr-2"
            />
            
            <Button 
              size="3"
              className="bg-yellow-400 hover:bg-yellow-500 text-black px-6"
              onClick={() => navigate('/upload-student-work')}
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              Upload Student Work
            </Button>
          </Flex>
        </Flex>
      </Box>

      {/* Submissions Table - Desktop and Mobile Responsive */}
      <Box className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {/* Desktop Table Header */}
        <Box className="hidden md:grid grid-cols-5 gap-6 p-4 border-b bg-gray-50 font-medium text-sm text-gray-600" style={{ gridTemplateColumns: '40px 2fr 2fr 3fr 1fr' }}>
          <Flex align="center">
            <Checkbox
              checked={isAllCurrentSubmissionsSelected()}
              onCheckedChange={handleSelectAllSubmissions}
            />
          </Flex>
          <Text>Class</Text>
          <Text>Student</Text>
          <Text>Submitted</Text>
          <Text>Score</Text>
        </Box>

        {submissions.length === 0 ? (
          <Box className="p-12 text-center">
            <Text size="4" className="text-gray-600 mb-6 block">
              There are no submissions yet.
            </Text>
            <Button onClick={() => navigate('/classes')}>
              View Available Classes
            </Button>
          </Box>
        ) : sortedAndFilteredSubmissions.length === 0 ? (
          <Box className="p-12 text-center">
            <Text size="4" className="text-gray-600 mb-6 block">
              No submissions match your current filters.
            </Text>
          </Box>
        ) : (
          sortedAndFilteredSubmissions.map((submission) => {
            const isFlagged = submission.submissions.some(sub => 
              sub.contentModeration?.isInappropriate === true
            );
            const { totalScore, totalMarks } = calculateTotalScore(submission.submissions);

            return (
              <Box key={submission.id}>
                {/* Desktop Layout */}
                <Box
                  className="hidden md:grid grid-cols-5 gap-6 p-4 border-b hover:bg-gray-50 cursor-pointer"
                  style={{ gridTemplateColumns: '40px 2fr 2fr 3fr 1fr' }}
                  onClick={() => handleViewSubmission(submission)}
                >
                  <Flex align="center">
                    <Box onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedSubmissions.includes(submission.id)}
                        onCheckedChange={(checked) => handleSubmissionSelection(submission.id, checked as boolean)}
                      />
                    </Box>
                  </Flex>
                  <Flex align="center" gap="2" className="font-medium">
                    <Text>{submission.class?.name || "Unknown Class"}</Text>
                    {isFlagged && <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />}
                  </Flex>
                  <Text>{submission.student?.name || "Unknown Student"}</Text>
                  <Text size="2" className="text-gray-600">
                    {formatDateTime(submission.submittedAt)}
                  </Text>
                  <Box>
                    <Badge className={getScoreColor(totalScore, totalMarks)}>
                      {totalScore}/{totalMarks}
                    </Badge>
                  </Box>
                </Box>

                {/* Mobile Card Layout */}
                <Box
                  className="block md:hidden p-4 border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewSubmission(submission)}
                >
                  <Flex direction="column" gap="3">
                    {/* Header Row - Class and Score */}
                    <Flex align="center" justify="between">
                      <Flex align="center" gap="3">
                        <Box onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedSubmissions.includes(submission.id)}
                            onCheckedChange={(checked) => handleSubmissionSelection(submission.id, checked as boolean)}
                          />
                        </Box>
                        <Flex align="center" gap="2">
                          <Text weight="medium" size="3">{submission.class?.name || "Unknown Class"}</Text>
                          {isFlagged && <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />}
                        </Flex>
                      </Flex>
                      <Badge className={getScoreColor(totalScore, totalMarks)}>
                        {totalScore}/{totalMarks}
                      </Badge>
                    </Flex>
                    
                    {/* Student Name */}
                    <Flex align="center" gap="2">
                      <Text size="2" className="text-gray-500 min-w-[60px]">Student:</Text>
                      <Text size="3">{submission.student?.name || "Unknown Student"}</Text>
                    </Flex>
                    
                    {/* Submission Date */}
                    <Flex align="center" gap="2">
                      <Text size="2" className="text-gray-500 min-w-[60px]">Submitted:</Text>
                      <Text size="2" className="text-gray-600">
                        {formatDateTime(submission.submittedAt)}
                      </Text>
                    </Flex>
                  </Flex>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Submission Details Modal */}
      <Dialog.Root open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <Dialog.Content className="max-w-4xl max-h-[90vh] overflow-hidden">
          <Box className="bg-yellow-400 -m-6 mb-0 p-6">
            <Flex justify="between" align="center">
              <Dialog.Title className="text-black text-lg">Submission Details</Dialog.Title>
              {selectedSubmission && (
                <PDFExportButton 
                  submissionData={selectedSubmission} 
                  isTeacherView={true}
                  size="2"
                  variant="outline"
                  className="bg-white hover:bg-gray-50 text-black border border-gray-300"
                />
              )}
            </Flex>
          </Box>

          {selectedSubmission && (
            <Box className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <Flex justify="between" align="center" className="mb-6 bg-gray-50 p-4 rounded">
                <Flex direction="column">
                  <Text size="2" className="text-gray-600">Class</Text>
                  <Text weight="medium">{selectedSubmission.class?.name}</Text>
                </Flex>
                <Flex direction="column">
                  <Text size="2" className="text-gray-600">Student</Text>
                  <Text weight="medium">{selectedSubmission.student?.name}</Text>
                </Flex>
                <Flex direction="column">
                  <Text size="2" className="text-gray-600">Submitted</Text>
                  <Text weight="medium">
                    {formatDateTime(selectedSubmission.submittedAt)}
                  </Text>
                </Flex>
              </Flex>

              <Tabs.Root defaultValue="questions" className="w-full">
                <Tabs.List className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg mb-6">
                  <Tabs.Trigger 
                    value="questions" 
                    className="px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
                  >
                    Questions & Answers
                  </Tabs.Trigger>
                  <Tabs.Trigger 
                    value="feedback" 
                    className="px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
                  >
                    Score & Feedback
                  </Tabs.Trigger>
                </Tabs.List>

                <Tabs.Content value="questions">
                  <Flex direction="column" gap="6">
                    {selectedSubmission.submissions.map((question, index) => (
                      <Card key={index}>
                        <Box className="p-4 border-b">
                          <Flex align="center" justify="between">
                            <Text size="5" weight="medium">Question {index + 1}</Text>
                            <Badge className={getScoreColor(question.score, question.marks)}>
                              Score: {question.score}/{question.marks}
                            </Badge>
                          </Flex>
                        </Box>
                        <Box className="p-4 space-y-4">
                          <Box>
                            <Text weight="medium" className="text-gray-900 mb-2 block">Question:</Text>
                            <Box className="bg-gray-50 p-3 rounded">
                              <Text className="text-gray-700">{question.question}</Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text weight="medium" className="text-gray-900 mb-2 block">Answer:</Text>
                            <Box className={`p-3 rounded border-l-4 ${
                              question.contentModeration?.isInappropriate 
                                ? 'bg-red-50 border-red-400' 
                                : 'bg-blue-50 border-blue-400'
                            }`}>
                              <Text className="text-gray-700 whitespace-pre-wrap">
                                {question.answer}
                              </Text>
                              {question.contentModeration?.isInappropriate && (
                                <Box className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                                  <Flex align="center" gap="2" className="text-red-700 mb-2">
                                    <Text weight="medium">🚩 Content Flagged</Text>
                                  </Flex>
                                  <Text size="2" className="text-red-600">
                                    <strong>Reason:</strong> {question.contentModeration.reason}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Flex>
                </Tabs.Content>

                <Tabs.Content value="feedback">
                  <Card>
                    <Box className="p-4 border-b">
                      <Flex align="center" justify="between">
                        <Text size="5" weight="medium">Score Details</Text>
                        <Flex align="center" gap="3">
                          <Badge 
                            className={getScoreColor(
                              isEditing && editedSubmission 
                                ? calculateTotalScore(editedSubmission.submissions).totalScore
                                : calculateTotalScore(selectedSubmission.submissions).totalScore,
                              isEditing && editedSubmission 
                                ? calculateTotalScore(editedSubmission.submissions).totalMarks
                                : calculateTotalScore(selectedSubmission.submissions).totalMarks
                            )}
                          >
                            Total: {isEditing && editedSubmission 
                              ? calculateTotalScore(editedSubmission.submissions).totalScore
                              : calculateTotalScore(selectedSubmission.submissions).totalScore}/
                            {isEditing && editedSubmission 
                              ? calculateTotalScore(editedSubmission.submissions).totalMarks
                              : calculateTotalScore(selectedSubmission.submissions).totalMarks}
                          </Badge>
                          {!isEditing ? (
                            <Button
                              size="2"
                              variant="outline"
                              onClick={() => handleEditSubmission(selectedSubmission)}
                              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                            >
                              <Pencil1Icon className="w-4 h-4 mr-2" />
                              Edit Feedback
                            </Button>
                          ) : (
                            <Flex gap="2">
                              <Button
                                size="2"
                                onClick={handleSaveEditedSubmission}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                disabled={Object.keys(scoreErrors).length > 0}
                              >
                                <CheckIcon className="w-4 h-4 mr-2" />
                                Save Changes
                              </Button>
                              <Button
                                size="2"
                                variant="outline"
                                onClick={handleCancelEdit}
                              >
                                <Cross2Icon className="w-4 h-4 mr-2" />
                                Cancel
                              </Button>
                            </Flex>
                          )}
                        </Flex>
                      </Flex>
                    </Box>
                    <Box className="p-4 space-y-6">
                      <Box>
                        <Text size="2" className="text-gray-600 mb-2 block">Overall Score</Text>
                        <Text size="4" weight="medium">
                          Scored {isEditing && editedSubmission 
                            ? calculateTotalScore(editedSubmission.submissions).totalScore
                            : calculateTotalScore(selectedSubmission.submissions).totalScore} out of {isEditing && editedSubmission 
                            ? calculateTotalScore(editedSubmission.submissions).totalMarks
                            : calculateTotalScore(selectedSubmission.submissions).totalMarks} marks
                        </Text>
                      </Box>

                      <Separator className="my-4" />

                      <Box>
                        <Text weight="medium" className="text-gray-900 mb-3 block">Detailed Feedback:</Text>
                        {(isEditing && editedSubmission ? editedSubmission.submissions : selectedSubmission.submissions).map((question, index) => (
                          <Box key={index} className="mb-6 last:mb-0">
                            <Flex align="center" gap="2" className="mb-2">
                              <Text weight="medium">Question {index + 1}</Text>
                              {!isEditing ? (
                                <Badge className={getScoreColor(question.score, question.marks)}>
                                  {question.score}/{question.marks}
                                </Badge>
                              ) : (
                                <Flex direction="column" align="start" gap="1">
                                  <Flex align="center" gap="2">
                                    <Text size="2" className="text-gray-600">Score:</Text>
                                    <TextField.Root
                                      type="number"
                                      min="0"
                                      max={question.marks || 0}
                                      value={question.score === 0 ? "0" : (question.score?.toString() || "")}
                                      onChange={(e) => handleScoreChange(index, e.target.value)}
                                      className="w-16"
                                      size="1"
                                    />
                                    <Text size="2" className="text-gray-600">/{question.marks}</Text>
                                  </Flex>
                                  {scoreErrors[index] && (
                                    <Text size="1" className="text-red-600">
                                      {scoreErrors[index]}
                                    </Text>
                                  )}
                                </Flex>
                              )}
                            </Flex>
                            <Box className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded space-y-3">
                              <Box>
                                <Text weight="medium" className="text-amber-800 block mb-1">AI Feedback:</Text>
                                {!isEditing ? (
                                  <Text className="text-gray-700 leading-relaxed">
                                    {question.feedback?.aiComment || 'No AI feedback available.'}
                                  </Text>
                                ) : (
                                  <TextArea
                                    value={question.feedback?.aiComment || ''}
                                    onChange={(e) => handleFeedbackChange(index, 'aiComment', e.target.value)}
                                    placeholder="Enter AI feedback..."
                                    className="min-h-[80px] w-full"
                                  />
                                )}
                              </Box>
                              <Box>
                                <Text weight="medium" className="text-amber-800 block mb-1">Suggestion for Improvement:</Text>
                                {!isEditing ? (
                                  <Text className="text-gray-700 leading-relaxed">
                                    {question.feedback?.suggestion || 'No suggestions available.'}
                                  </Text>
                                ) : (
                                  <TextArea
                                    value={question.feedback?.suggestion || ''}
                                    onChange={(e) => handleFeedbackChange(index, 'suggestion', e.target.value)}
                                    placeholder="Enter suggestions for improvement..."
                                    className="min-h-[80px] w-full"
                                  />
                                )}
                              </Box>
                              <Box>
                                <Text weight="medium" className="text-amber-800 block mb-1">Mark Range Explanation:</Text>
                                {!isEditing ? (
                                  <Text className="text-gray-700 leading-relaxed">
                                    {question.feedback?.markRange || 'No mark range explanation available.'}
                                  </Text>
                                ) : (
                                  <TextArea
                                    value={question.feedback?.markRange || ''}
                                    onChange={(e) => handleFeedbackChange(index, 'markRange', e.target.value)}
                                    placeholder="Explain the mark range..."
                                    className="min-h-[80px] w-full"
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Card>
                </Tabs.Content>
              </Tabs.Root>
            </Box>
          )}
          
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="outline">Close</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>


    </Box>
    </>
  );
};

export default TeacherSubmissions; 